import { IPoint2d } from '@/editor/math/IPoint2d';
import { getUniqueId } from '@/editor/utils';
import {
  IConnectRelateSeparateWallInfo,
  ESeparateWallType,
  SeparateWallData,
  TArcSeparateWallData,
  ISpecificConnectRelateSeparateWallInfo
} from './SeparateWall';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { calLeftRightIntersection, equalsNumber, sortConnectedLinesClockwise } from '@/editor/math/utils';
import { EEntityStatus } from '@/editor/types/entity';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { SeparateLineWall } from '../type';
import { OVERLAPLENGTH } from '@/editor/math/const';
import { Curve2dUtils } from '@/editor/math/Curve2dUtils';
import { Line2d } from '@/editor/math/Line2d';
import { EFaceSide, IRotatedBoxPure } from '@/editor/types';
import { calAuxiliarySegsCore, getBaseShape, sampleShapePath } from '@/editor/utils/wallShape';
import { IShapeSeg2d } from '@/editor/types/shape';

export function initDrawSeparateWallInfo(baseData: {
  start: IPoint2d;
  end: IPoint2d;
  thickness: number;
  center?: IPoint2d;
  clockwise?: boolean;
}) {
  const { baseShape, startShape, endShape } = getBaseShape(baseData);
  return {
    drawShape: baseShape,
    startShape: startShape,
    endShape: endShape
  };
}

export function createSeparateWallData(params: {
  roomId: string;
  start: IPoint2d;
  end: IPoint2d;
  thickness: number;
  height: number;
  offGround: number;
  type: ESeparateWallType;

  startId?: number;
  endId?: number;
  relativeWallId?: string;
  relativeWallSide?: EFaceSide;
  side?: EFaceSide;
  center?: IPoint2d;
  clockwise?: boolean;
}) {
  const thickness = params.type === ESeparateWallType.VirtualWall ? 0 : params.thickness;

  const separateWallData: SeparateWallData = {
    id: getUniqueId(),
    start: params.start,
    end: params.end,
    thickness,
    height: params.height,
    offGround: params.offGround,
    type: params.type,
    relateRoomId: params.roomId,
    // connectWallIds: [],
    // connectWallSide: EFaceSide.Empty,
    // connectWallSides: [],
    relativeWallId: params.relativeWallId || '',
    relativeWallSide: params.relativeWallSide || EFaceSide.Empty,
    startId: params.startId || 0,
    endId: params.endId || 0,
    side: params.side || EFaceSide.Empty,
    center: params.center,
    clockwise: params.clockwise,

    drawSeparateWallInfo: initDrawSeparateWallInfo({
      start: params.start,
      end: params.end,
      thickness,
      center: params.center,
      clockwise: params.clockwise
    })
  };

  return separateWallData;
}

export function separateWallBaseShape(mainSeparateWallData: SeparateWallData) {
  const mainLeg = new LineSeg2d(
    Point2dUtils.clone(mainSeparateWallData.start),
    Point2dUtils.clone(mainSeparateWallData.end)
  );
  const { leftCurve, rightCurve } = Curve2dUtils.offsetCurves(mainLeg, mainSeparateWallData.thickness / 2);

  return [rightCurve.start, mainLeg.start, leftCurve.start, leftCurve.end, mainLeg.end, rightCurve.end];
}

export function getStartEndConnectSeparateWalls(
  mainSeparateWallData: SeparateWallData,
  separateWallDatas: SeparateWallData[]
) {
  const startConnectSeparateWalls: SeparateWallData[] = [];
  const endConnectSeparateWalls: SeparateWallData[] = [];
  for (const separateWallData of separateWallDatas) {
    if (equalsSeparateWallData(separateWallData, mainSeparateWallData)) {
      continue;
    }

    if (
      Point2dUtils.equals(mainSeparateWallData.start, separateWallData.start) ||
      Point2dUtils.equals(mainSeparateWallData.start, separateWallData.end)
    ) {
      startConnectSeparateWalls.push(separateWallData);
    }

    if (
      Point2dUtils.equals(mainSeparateWallData.end, separateWallData.start) ||
      Point2dUtils.equals(mainSeparateWallData.end, separateWallData.end)
    ) {
      endConnectSeparateWalls.push(separateWallData);
    }
  }

  return { startConnectSeparateWalls, endConnectSeparateWalls };
}

function getSeparateWallThicknesses(
  mainSeparateWallData: SeparateWallData,
  connectLineSegs: LineSeg2d[],
  connectSeparateWalls: SeparateWallData[]
) {
  const thicknesses: number[] = [];
  for (const connectLineSeg of connectLineSegs) {
    let hasEqual = false;
    for (const connectSeparateWall of connectSeparateWalls) {
      if (
        Point2dUtils.equals(connectSeparateWall.start, connectLineSeg.start) &&
        Point2dUtils.equals(connectSeparateWall.end, connectLineSeg.end)
      ) {
        thicknesses.push(connectSeparateWall.thickness);
        hasEqual = true;
        break;
      }
    }

    if (!hasEqual) {
      thicknesses.push(mainSeparateWallData.thickness);
    }
  }

  return thicknesses;
}

export function calSeparateWallOutline(
  mainSeparateWallData: SeparateWallData,
  separateWallDatas: SeparateWallData[],
  baseStartConnectSeparateWalls: SeparateWallData[],
  baseEndConnectSeparateWalls: SeparateWallData[]
) {
  const { startConnectSeparateWalls, endConnectSeparateWalls } = getStartEndConnectSeparateWalls(
    mainSeparateWallData,
    separateWallDatas
  );
  const mainLineSeg = new LineSeg2d(
    Point2dUtils.clone(mainSeparateWallData.start),
    Point2dUtils.clone(mainSeparateWallData.end)
  );

  const startTempConnectLineSegs: LineSeg2d[] = [];
  for (const startConnectSeparateWall of startConnectSeparateWalls) {
    startTempConnectLineSegs.push(
      new LineSeg2d(
        Point2dUtils.clone(startConnectSeparateWall.start),
        Point2dUtils.clone(startConnectSeparateWall.end)
      )
    );
  }
  const startConnectLineSegs: LineSeg2d[] = startTempConnectLineSegs.length
    ? sortConnectedLinesClockwise(mainLineSeg, startTempConnectLineSegs, true)
    : [];

  const startThicknesses = getSeparateWallThicknesses(
    mainSeparateWallData,
    startConnectLineSegs,
    startConnectSeparateWalls
  );

  const endTempConnectLineSegs: LineSeg2d[] = [];
  for (const endConnectSeparateWall of endConnectSeparateWalls) {
    endTempConnectLineSegs.push(
      new LineSeg2d(Point2dUtils.clone(endConnectSeparateWall.start), Point2dUtils.clone(endConnectSeparateWall.end))
    );
  }
  const endConnectLineSegs: LineSeg2d[] = endTempConnectLineSegs.length
    ? sortConnectedLinesClockwise(mainLineSeg, endTempConnectLineSegs, false)
    : [];
  const endThicknesses = getSeparateWallThicknesses(mainSeparateWallData, endConnectLineSegs, endConnectSeparateWalls);

  const { mainLeft: mainStartLeft, mainRight: mainStartRight } = calLeftRightIntersection(
    mainLineSeg,
    mainSeparateWallData.thickness / 2,
    startConnectLineSegs,
    startThicknesses,
    true
  );
  const { mainLeft: mainEndLeft, mainRight: mainEndRight } = calLeftRightIntersection(
    mainLineSeg,
    mainSeparateWallData.thickness / 2,
    endConnectLineSegs,
    endThicknesses,
    false
  );

  const { leftCurve: mainLeftCurve, rightCurve: mainRightcurve } = Curve2dUtils.offsetCurves(
    mainLineSeg,
    mainSeparateWallData.thickness / 2
  );

  const leftLine = new Line2d({
    points: {
      p1: Point2dUtils.clone(mainLeftCurve.start),
      p2: Point2dUtils.clone(mainLeftCurve.end)
    }
  });
  const rightLine = new Line2d({
    points: {
      p1: Point2dUtils.clone(mainRightcurve.start),
      p2: Point2dUtils.clone(mainRightcurve.end)
    }
  });

  const leftCenter = Point2dUtils.getCenter(mainLeftCurve.start, mainLeftCurve.end);
  const rightCenter = Point2dUtils.getCenter(mainRightcurve.start, mainRightcurve.end);
  let startLeftPoint = Point2dUtils.clone(mainStartLeft);
  let startRightPoint = Point2dUtils.clone(mainStartRight);
  let endLeftPoint = Point2dUtils.clone(mainEndLeft);
  let endRightPoint = Point2dUtils.clone(mainEndRight);
  let startLeftFirst = false;
  let startRightFirst = false;
  for (const baseStartConnectSeparateWall of baseStartConnectSeparateWalls) {
    const baseStartLine = new Line2d({
      points: {
        p1: Point2dUtils.clone(baseStartConnectSeparateWall.start),
        p2: Point2dUtils.clone(baseStartConnectSeparateWall.end)
      }
    });

    const baseStartLeftIntersect = leftLine.intersection(baseStartLine);
    if (baseStartLeftIntersect) {
      if (startConnectSeparateWalls.length) {
        if (
          Point2dUtils.distanceTo(leftCenter, startLeftPoint) >
          Point2dUtils.distanceTo(leftCenter, baseStartLeftIntersect)
        ) {
          startLeftPoint = baseStartLeftIntersect;
        }
      } else {
        if (!startLeftFirst) {
          startLeftPoint = baseStartLeftIntersect;
          startLeftFirst = true;
        } else {
          if (
            Point2dUtils.distanceTo(leftCenter, startLeftPoint) >
            Point2dUtils.distanceTo(leftCenter, baseStartLeftIntersect)
          ) {
            startLeftPoint = baseStartLeftIntersect;
          }
        }
      }
    }

    const baseStartRightIntersect = rightLine.intersection(baseStartLine);
    if (baseStartRightIntersect) {
      if (startConnectSeparateWalls.length) {
        if (
          Point2dUtils.distanceTo(rightCenter, startRightPoint) >
          Point2dUtils.distanceTo(rightCenter, baseStartRightIntersect)
        ) {
          startRightPoint = baseStartRightIntersect;
        }
      } else {
        if (!startRightFirst) {
          startRightPoint = baseStartRightIntersect;
          startRightFirst = true;
        } else {
          if (
            Point2dUtils.distanceTo(rightCenter, startRightPoint) >
            Point2dUtils.distanceTo(rightCenter, baseStartRightIntersect)
          ) {
            startRightPoint = baseStartRightIntersect;
          }
        }
      }
    }
  }

  let endLeftFirst = false;
  let endRightFirst = false;
  for (const baseEndConnectSeparateWall of baseEndConnectSeparateWalls) {
    const baseEndLine = new Line2d({
      points: {
        p1: Point2dUtils.clone(baseEndConnectSeparateWall.start),
        p2: Point2dUtils.clone(baseEndConnectSeparateWall.end)
      }
    });

    const baseEndLeftIntersect = leftLine.intersection(baseEndLine);
    if (baseEndLeftIntersect) {
      if (endConnectSeparateWalls.length) {
        if (
          Point2dUtils.distanceTo(leftCenter, endLeftPoint) > Point2dUtils.distanceTo(leftCenter, baseEndLeftIntersect)
        ) {
          endLeftPoint = baseEndLeftIntersect;
        }
      } else {
        if (!endLeftFirst) {
          endLeftPoint = baseEndLeftIntersect;
          endLeftFirst = true;
        } else {
          if (
            Point2dUtils.distanceTo(leftCenter, endLeftPoint) >
            Point2dUtils.distanceTo(leftCenter, baseEndLeftIntersect)
          ) {
            endLeftPoint = baseEndLeftIntersect;
          }
        }
      }
    }

    const baseEndRightIntersect = rightLine.intersection(baseEndLine);
    if (baseEndRightIntersect) {
      if (endConnectSeparateWalls.length) {
        if (
          Point2dUtils.distanceTo(rightCenter, endRightPoint) >
          Point2dUtils.distanceTo(rightCenter, baseEndRightIntersect)
        ) {
          endRightPoint = baseEndRightIntersect;
        }
      } else {
        if (!endRightFirst) {
          endRightPoint = baseEndRightIntersect;
          endRightFirst = true;
        } else {
          if (
            Point2dUtils.distanceTo(rightCenter, endRightPoint) >
            Point2dUtils.distanceTo(rightCenter, baseEndRightIntersect)
          ) {
            endRightPoint = baseEndRightIntersect;
          }
        }
      }
    }
  }

  return [
    startRightPoint,
    Point2dUtils.clone(mainSeparateWallData.start),
    startLeftPoint,
    endLeftPoint,
    Point2dUtils.clone(mainSeparateWallData.end),
    endRightPoint
  ];
}

export function equalsSeparateWallData(separateData1: SeparateWallData, separateData2: SeparateWallData) {
  if (
    Point2dUtils.equals(separateData1.start, separateData2.start) &&
    Point2dUtils.equals(separateData1.end, separateData2.end) &&
    equalsNumber(separateData1.thickness, separateData2.thickness) &&
    equalsNumber(separateData1.height, separateData2.height) &&
    equalsNumber(separateData1.offGround, separateData2.offGround)
  ) {
    return true;
  }

  return false;
}

export function separateWallDatasToLineSegs(separateWallDatas: SeparateWallData[]): SeparateLineWall[] {
  const separateLineWalls: SeparateLineWall[] = [];
  separateWallDatas.forEach((separateWall) => {
    const lineSeg = new LineSeg2d(Point2dUtils.clone(separateWall.start), Point2dUtils.clone(separateWall.end));
    separateLineWalls.push({
      lineSeg,
      thickness: separateWall.thickness,
      height: separateWall.height,
      offGround: separateWall.offGround,
      type: separateWall.type
    });
  });

  return separateLineWalls;
}

export function getSeparateWallStatus(
  separateWallDatas: SeparateWallData[],
  baseSeparateWallDatas: SeparateWallData[],
  otherSegs: LineSeg2d[],
  start: IPoint2d,
  end: IPoint2d
) {
  let status = EEntityStatus.normal;
  const virtualSeg = new LineSeg2d(start, end);
  for (const otherSeg of otherSegs) {
    const intersect = virtualSeg.intersection(otherSeg);
    if (intersect) {
      status = EEntityStatus.unnormal;
      return status;
    }
  }

  for (const separateWallData of separateWallDatas) {
    const separateSeg = new LineSeg2d(separateWallData.start, separateWallData.end);

    const intersect = virtualSeg.intersection(separateSeg);
    if (
      intersect &&
      !Point2dUtils.equals(intersect, separateSeg.start) &&
      !Point2dUtils.equals(intersect, separateSeg.end) &&
      !Point2dUtils.equals(intersect, start) &&
      !Point2dUtils.equals(intersect, end)
    ) {
      status = EEntityStatus.unnormal;
      return status;
    } else if (virtualSeg.isCollinear(separateSeg)) {
      const overlapSeg = virtualSeg.getOverlapSegment(separateSeg);
      if (overlapSeg && overlapSeg.length > OVERLAPLENGTH) {
        status = EEntityStatus.unnormal;
        return status;
      }
    }
  }

  for (const baseSeparateWallData of baseSeparateWallDatas) {
    const baseSeparateSeg = new LineSeg2d(baseSeparateWallData.start, baseSeparateWallData.end);

    if (virtualSeg.isCollinear(baseSeparateSeg)) {
      const overlapSeg = virtualSeg.getOverlapSegment(baseSeparateSeg);
      if (overlapSeg && overlapSeg.length > OVERLAPLENGTH) {
        status = EEntityStatus.unnormal;
        break;
      }
    } else {
      const intersect = virtualSeg.intersection(baseSeparateSeg);
      if (
        intersect &&
        !Point2dUtils.equals(virtualSeg.start, intersect) &&
        !Point2dUtils.equals(virtualSeg.end, intersect)
      ) {
        status = EEntityStatus.unnormal;
        break;
      }
    }
  }

  return status;
}

export function findIndexSeparateWallData(
  mainSeparateWallData: SeparateWallData,
  separateWallDatas: SeparateWallData[]
) {
  const index = separateWallDatas.findIndex((wallData) => equalsSeparateWallData(wallData, mainSeparateWallData));
  return index;
}

// 获取连接搭接的墙体
export function getConnectLapSeparateWalls(
  mainSeparateWallData: SeparateWallData,
  separateWallDatas: SeparateWallData[]
) {
  const mainSeparateWallSeg = new LineSeg2d(mainSeparateWallData.start, mainSeparateWallData.end);

  const startConnectSeparateWalls: SeparateWallData[] = [];
  const endConnectSeparateWalls: SeparateWallData[] = [];
  const otherConnectSeparateWalls: SeparateWallData[] = [];
  for (const separateWallData of separateWallDatas) {
    if (
      equalsSeparateWallData(separateWallData, mainSeparateWallData) ||
      separateWallData.type === ESeparateWallType.VirtualWall
    ) {
      continue;
    }

    const separateWallSeg = new LineSeg2d(separateWallData.start, separateWallData.end);
    if (separateWallSeg.pointOnSegment(mainSeparateWallData.start)) {
      startConnectSeparateWalls.push(separateWallData);
    }

    if (separateWallSeg.pointOnSegment(mainSeparateWallData.end)) {
      endConnectSeparateWalls.push(separateWallData);
    }

    const startIndex = findIndexSeparateWallData(separateWallData, startConnectSeparateWalls);
    const endIndex = findIndexSeparateWallData(separateWallData, endConnectSeparateWalls);
    if (startIndex === -1 && endIndex === -1 && mainSeparateWallSeg.intersection(separateWallSeg)) {
      otherConnectSeparateWalls.push(separateWallData);
    }
  }

  return { startConnectSeparateWalls, endConnectSeparateWalls, otherConnectSeparateWalls };
}

export function getAllConnectSeparateWallInfos(
  mainSeparateWallData: SeparateWallData,
  separateWallDatas: SeparateWallData[]
): IConnectRelateSeparateWallInfo[] {
  const connectSeparateWallInfos: IConnectRelateSeparateWallInfo[] = [];
  const mainSeparateWallSeg = new LineSeg2d(
    Point2dUtils.clone(mainSeparateWallData.start),
    Point2dUtils.clone(mainSeparateWallData.end)
  );
  const isVirtualWall =
    mainSeparateWallData.type === ESeparateWallType.BaseVirtualWall ||
    mainSeparateWallData.type === ESeparateWallType.VirtualWall;
  for (const separateWallData of separateWallDatas) {
    if (equalsSeparateWallData(mainSeparateWallData, separateWallData)) {
      continue;
    }

    const separateWallSeg = new LineSeg2d(
      Point2dUtils.clone(separateWallData.start),
      Point2dUtils.clone(separateWallData.end)
    );
    const intersectPoint = mainSeparateWallSeg.intersection(separateWallSeg);
    if (intersectPoint) {
      connectSeparateWallInfos.push({ separateWallData, connectPoint: intersectPoint });
    } else if (
      Point2dUtils.equals(mainSeparateWallData.start, separateWallData.start) ||
      Point2dUtils.equals(mainSeparateWallData.start, separateWallData.end)
    ) {
      connectSeparateWallInfos.push({ separateWallData, connectPoint: mainSeparateWallData.start });
    } else if (
      Point2dUtils.equals(mainSeparateWallData.end, separateWallData.start) ||
      Point2dUtils.equals(mainSeparateWallData.end, separateWallData.end)
    ) {
      connectSeparateWallInfos.push({ separateWallData, connectPoint: mainSeparateWallData.end });
    } else if (
      !isVirtualWall &&
      separateWallData.type !== ESeparateWallType.BaseVirtualWall &&
      separateWallData.type !== ESeparateWallType.VirtualWall
    ) {
      const baseSeparateWallRect = getBaseShape(separateWallData).baseShape;
      const mainSeparateWallRect = getBaseShape(mainSeparateWallData).baseShape;
      const result = polygonsIntersect(baseSeparateWallRect, mainSeparateWallRect);
      if (result) {
        connectSeparateWallInfos.push({
          separateWallData,
          connectPoint: result
        });
      }
    }
  }

  return connectSeparateWallInfos;
}

const getEdges = (points: IPoint2d[]) => {
  const edges = [];
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    edges.push([p1, p2]);
  }
  return edges;
};

// 只检测相交，不检测完全包含
function polygonsIntersect(poly1: IPoint2d[], poly2: IPoint2d[]) {
  if (poly1.length < 3 && poly2.length < 3) return false;

  // 检查边是否相交
  const edges1 = getEdges(poly1);
  const edges2 = getEdges(poly2);

  for (const [p1, p2] of edges1) {
    const line1 = new LineSeg2d(p1, p2);
    for (const [p3, p4] of edges2) {
      const intersect = line1.intersection(new LineSeg2d(p3, p4));
      if (intersect) return intersect;
    }
  }

  return false;
}

export function getSeparateWallOutlineSegs(separateWallData: SeparateWallData) {
  let shape = separateWallData.drawSeparateWallInfo.drawShape;
  if (!shape.length) {
    shape = separateWallBaseShape(separateWallData);
  }

  const outlineSegs: LineSeg2d[] = [];
  const len = shape.length;
  for (let i = 0; i < len; i++) {
    const start = shape[i];
    const end = shape[(i + 1) % len];
    outlineSegs.push(new LineSeg2d(start, end));
  }

  return outlineSegs;
}

/**
 * 隔墙轮廓的离散点，以及轮廓是否成环。
 *
 * drawShape 为空时用 getBaseShape 兜底，与 initDrawSeparateWallInfo 一致，弧墙不会退化成直墙。
 * 零厚度的虚拟隔墙拿到的是中心线，没有内部区域，不成环。
 *
 * closed 必须按离散前的 shape 判断：弧形中心线离散后点数同样会大于 2，
 * 用点数判断会把它误认成闭合轮廓，凭空多出一条首尾相连的弦。
 */
export function getSeparateWallOutline(separateWallData: SeparateWallData): {
  points: IPoint2d[];
  closed: boolean;
} {
  let shape = separateWallData.drawSeparateWallInfo.drawShape;
  if (!shape.length) {
    shape = getBaseShape(separateWallData).baseShape;
  }

  const closed = shape.length > 2;
  const points = sampleShapePath(shape);
  // 兜底后的 shape 仍为空时退回中心线
  if (!points.length) return { points: [separateWallData.start, separateWallData.end], closed: false };

  return { points, closed };
}

/**
 * 隔墙的轴对齐包围盒（rotation 恒为 0）。
 * 隔墙没有 width/rotation，盒中心也不是数据里的 center（那是弧墙圆心），
 * 因此由墙体轮廓离散后的点集求 min/max 得到，弧墙的凸出量会被包含进来。
 */
export function getSeparateWallBox(separateWallData: SeparateWallData): IRotatedBoxPure {
  const { points } = getSeparateWallOutline(separateWallData);

  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2,
    z: separateWallData.offGround + separateWallData.height / 2,
    width: maxX - minX,
    thickness: maxY - minY,
    height: separateWallData.height,
    rotation: 0
  };
}

export function calAuxiliarySegs(separateWallData: SeparateWallData): IShapeSeg2d[] {
  let shape = separateWallData.drawSeparateWallInfo.drawShape;
  if (!shape.length) {
    shape = separateWallBaseShape(separateWallData);
  }

  // 已落地隔墙始终传入轮廓 shape，分隔线裁剪到墙体轮廓
  return calAuxiliarySegsCore({
    start: separateWallData.start,
    end: separateWallData.end,
    thickness: separateWallData.thickness,
    type: separateWallData.type,
    center: separateWallData.center,
    clockwise: separateWallData.clockwise,
    shape
  });
}

// 连接的墙体
function pointConnectRelateSeparateWalls(
  mainSeparateWallData: SeparateWallData,
  point: IPoint2d,
  separateWallDatas: SeparateWallData[]
) {
  const connectSeparateWallDataInfos: IConnectRelateSeparateWallInfo[] = [];
  for (const separateWallData of separateWallDatas) {
    if (equalsSeparateWallData(mainSeparateWallData, separateWallData)) {
      continue;
    }

    if (Point2dUtils.equals(point, separateWallData.start) || Point2dUtils.equals(point, separateWallData.end)) {
      connectSeparateWallDataInfos.push({ separateWallData, connectPoint: point });
    }
  }

  return connectSeparateWallDataInfos;
}

// 搭接的墙体
function pointLapRelateSeparateWalls(
  mainSeparateWallData: SeparateWallData,
  point: IPoint2d,
  separateWallDatas: SeparateWallData[]
) {
  const lapWallDataInfos: IConnectRelateSeparateWallInfo[] = [];
  for (const separateWallData of separateWallDatas) {
    if (equalsSeparateWallData(mainSeparateWallData, separateWallData)) {
      continue;
    }

    // 连接的跳过，不能算搭接
    if (Point2dUtils.equals(point, separateWallData.start) || Point2dUtils.equals(point, separateWallData.end)) {
      continue;
    }

    const wallSeg = new LineSeg2d(Point2dUtils.clone(separateWallData.start), Point2dUtils.clone(separateWallData.end));

    if (wallSeg.pointOnSegment(point)) {
      lapWallDataInfos.push({ separateWallData, connectPoint: point });
    }
  }

  return lapWallDataInfos;
}

// 起始点连接在主隔墙上的关联隔墙
export function connectRelateSeparateWallInfo(
  mainSeparateWallData: SeparateWallData,
  separateWallDatas: SeparateWallData[]
) {
  const mainSeparateWallSeg = new LineSeg2d(
    Point2dUtils.clone(mainSeparateWallData.start),
    Point2dUtils.clone(mainSeparateWallData.end)
  );

  const relateSeparateWallDataInfos: IConnectRelateSeparateWallInfo[] = [];
  // 先获取与主墙体起始点连接的墙体（有且只有一堵的时候才能判断为关联墙体)
  const startConnectSeparateWallDataInfos = pointConnectRelateSeparateWalls(
    mainSeparateWallData,
    mainSeparateWallData.start,
    separateWallDatas
  );
  const endConnectSeparateWallDataInfos = pointConnectRelateSeparateWalls(
    mainSeparateWallData,
    mainSeparateWallData.end,
    separateWallDatas
  );

  const startLapSeparateWallDataInfos = pointLapRelateSeparateWalls(
    mainSeparateWallData,
    mainSeparateWallData.start,
    separateWallDatas
  );
  const endLapSeparateWallDataInfos = pointLapRelateSeparateWalls(
    mainSeparateWallData,
    mainSeparateWallData.end,
    separateWallDatas
  );

  const startOnlyConnectRelateSeparateWallInfo =
    startConnectSeparateWallDataInfos.length === 1 ? startConnectSeparateWallDataInfos[0] : undefined;
  const endOnlyConnectRelateSeparateWallInfo =
    endConnectSeparateWallDataInfos.length === 1 ? endConnectSeparateWallDataInfos[0] : undefined;

  for (const separateWallData of separateWallDatas) {
    // 自身还有起始点相连的不参与之后的计算
    if (equalsSeparateWallData(mainSeparateWallData, separateWallData)) {
      continue;
    }

    if (mainSeparateWallSeg.pointOnSegment(separateWallData.start)) {
      if (
        (startOnlyConnectRelateSeparateWallInfo &&
          equalsSeparateWallData(separateWallData, startOnlyConnectRelateSeparateWallInfo.separateWallData)) ||
        (endOnlyConnectRelateSeparateWallInfo &&
          equalsSeparateWallData(separateWallData, endOnlyConnectRelateSeparateWallInfo.separateWallData))
      ) {
        continue;
      }
      relateSeparateWallDataInfos.push({
        separateWallData,
        connectPoint: Point2dUtils.clone(separateWallData.start)
      });
    } else if (mainSeparateWallSeg.pointOnSegment(separateWallData.end)) {
      if (
        (startOnlyConnectRelateSeparateWallInfo &&
          equalsSeparateWallData(separateWallData, startOnlyConnectRelateSeparateWallInfo.separateWallData)) ||
        (endOnlyConnectRelateSeparateWallInfo &&
          equalsSeparateWallData(separateWallData, endOnlyConnectRelateSeparateWallInfo.separateWallData))
      ) {
        continue;
      }
      relateSeparateWallDataInfos.push({
        separateWallData,
        connectPoint: Point2dUtils.clone(separateWallData.end)
      });
    }
  }
  const specificConnectRelateWallInfo: ISpecificConnectRelateSeparateWallInfo = {
    startOnlyConnectRelateSeparateWallInfo,
    endOnlyConnectRelateSeparateWallInfo,
    startLapSeparateWallDataInfos,
    endLapSeparateWallDataInfos,
    startConnectRelateSeparateWallInfos: startConnectSeparateWallDataInfos,
    endConnectRelateSeparateWallInfos: endConnectSeparateWallDataInfos,
    otherConnectRelateSeparateWallInfos: relateSeparateWallDataInfos
  };

  return specificConnectRelateWallInfo;
}

// 主墙体首尾会连接的墙体信息
export function connectStartEndRelateSeparateWallInfo(
  mainSeparateWallData: SeparateWallData,
  separateWallDatas: SeparateWallData[]
) {
  const startConnectSeparateWalls: SeparateWallData[] = [];
  const endConnectSeparateWalls: SeparateWallData[] = [];
  for (const separateWallData of separateWallDatas) {
    if (equalsSeparateWallData(mainSeparateWallData, separateWallData)) {
      continue;
    }

    const separateWallSeg = new LineSeg2d(
      Point2dUtils.clone(separateWallData.start),
      Point2dUtils.clone(separateWallData.end)
    );
    if (separateWallSeg.pointOnSegment(mainSeparateWallData.start)) {
      startConnectSeparateWalls.push(separateWallData);
    } else if (separateWallSeg.pointOnSegment(mainSeparateWallData.end)) {
      endConnectSeparateWalls.push(separateWallData);
    }
  }

  return { startConnectSeparateWalls, endConnectSeparateWalls };
}

export function rejectSeparateWallData(
  separateWallDatas: SeparateWallData[],
  rejectSeparateWallDatas: SeparateWallData[]
) {
  const resultSeparateWallDatas: SeparateWallData[] = [];
  for (const separateWallData of separateWallDatas) {
    let hasStartEnd = false;
    for (const rejectSeparateWallData of rejectSeparateWallDatas) {
      if (equalsSeparateWallData(rejectSeparateWallData, separateWallData)) {
        hasStartEnd = true;
        break;
      }
    }

    if (!hasStartEnd) {
      resultSeparateWallDatas.push(separateWallData);
    }
  }

  return resultSeparateWallDatas;
}

export function isArcSeparateWallData(separateData: SeparateWallData): separateData is TArcSeparateWallData {
  return separateData.center !== undefined && separateData.clockwise !== undefined;
}
